import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Spinner, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { CustomToastContent } from '../CustomToastContent';
import { InfoModal } from '../InfoModal';
import { toastOptionsTopRight } from '../../utils/Enums/ToastOptions';
import { FleetApproval, FleetWaybillSettings } from '../../models/fleet-models';
import { AromaInfo } from '../../models/configuration-models';
import { fetchFleetWaybillSettings, showFleetError, updateFleetWaybillSettings } from '../../utils/fleet-hooks';
// Името започва с use, но е обикновена заявка - преименува се, за да не я вземе eslint за hook.
import { useGetConfigCompanyInfo as fetchCompanyInfo } from '../../utils/configuration-hooks';

interface CompanyForm {
    eik: string;
    address: string;
    manager: string;
}

const EIK_PATTERN = /^(\d{9}|\d{13})?$/;

const companyOf = (settings: FleetWaybillSettings): CompanyForm => ({
    eik: settings.eik ?? '',
    address: settings.address ?? '',
    manager: settings.manager ?? '',
});

const trimmed = (form: CompanyForm): CompanyForm => ({
    eik: form.eik.trim(),
    address: form.address.trim(),
    manager: form.manager.trim(),
});

const sameCompany = (a: CompanyForm, b: CompanyForm) => {
    const left = trimmed(a);
    const right = trimmed(b);
    return left.eik === right.eik && left.address === right.address && left.manager === right.manager;
};

/**
 * Настройките на пътните листи в GPS Areon: начинът на одобряване (по дни / по месеци) и данните на
 * фирмата в горната част на месечния пътен лист. Пазят се само в GPS Areon. Двете части се записват
 * поотделно; GPS Areon приема всичко наведнъж, затова другата част се праща такава, каквато е записана.
 */
export const FleetWaybillSettingsOptions = () => {
    const { t } = useTranslation('translation', { keyPrefix: 'fleet' });
    const [settings, setSettings] = useState<FleetWaybillSettings>();
    const [ourCompany, setOurCompany] = useState<AromaInfo>();
    const [approval, setApproval] = useState<FleetApproval>('day');
    const [company, setCompany] = useState<CompanyForm>({ eik: '', address: '', manager: '' });
    const [error, setError] = useState<string>();
    const [isLoading, setIsLoading] = useState(false);
    const [saving, setSaving] = useState<'approval' | 'company'>();
    const [confirming, setConfirming] = useState<'approval' | 'company'>();

    const apply = (saved: FleetWaybillSettings) => {
        setSettings(saved);
        setApproval(saved.approval ?? 'day');
        setCompany(companyOf(saved));
    };

    const load = () => {
        setIsLoading(true);
        setError(undefined);
        Promise.all([fetchFleetWaybillSettings(), fetchCompanyInfo().catch(() => undefined)])
            .then(([loaded, aromaInfo]) => {
                // При някои грешки interceptor-ът връща тялото на грешката вместо да откаже.
                if (!loaded?.companyId) {
                    throw new Error('No GPS Areon company');
                }
                apply(loaded);
                setOurCompany(aromaInfo);
            })
            .catch(() => setError(t('settingsLoadError')))
            .finally(() => setIsLoading(false));
    };

    useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

    const canEdit = !!settings?.canEdit;
    const savedApproval = settings?.approval ?? 'day';
    const savedCompany = settings ? companyOf(settings) : company;
    const approvalChanged = approval !== savedApproval;
    const companyChanged = !sameCompany(company, savedCompany);
    const eikInvalid = !EIK_PATTERN.test(company.eik.trim());

    const ours: CompanyForm | undefined = ourCompany && {
        eik: ourCompany.vat?.trim() ?? '',
        address: ourCompany.address?.trim() ?? '',
        manager: ourCompany.owner?.trim() ?? '',
    };
    const differsFromOurs = !!ours && !sameCompany(company, ours);
    const nameDiffers =
        !!settings?.name && !!ourCompany?.companyName && settings.name.trim() !== ourCompany.companyName.trim();

    const approvalLabel = (value: FleetApproval) => t(value === 'month' ? 'approvalMonth' : 'approvalDay');
    const changes: { label: string; from: string; to: string }[] =
        confirming === 'approval'
            ? [{ label: t('approvalModeLabel'), from: approvalLabel(savedApproval), to: approvalLabel(approval) }]
            : confirming === 'company'
            ? (
                  [
                      ['eik', t('eik')],
                      ['address', t('address')],
                      ['manager', t('companyManager')],
                  ] as [keyof CompanyForm, string][]
              )
                  .map(([key, label]) => ({ label, from: savedCompany[key].trim(), to: company[key].trim() }))
                  .filter((change) => change.from !== change.to)
            : [];

    const save = async (part: 'approval' | 'company', request: CompanyForm & { approval: FleetApproval }) => {
        setSaving(part);
        try {
            apply(await updateFleetWaybillSettings({ ...trimmed(request), approval: request.approval }));
            toast.success(
                <CustomToastContent content={[t(part === 'approval' ? 'approvalSaved' : 'companySaved')]} />,
                toastOptionsTopRight
            );
        } catch (error) {
            await showFleetError(error);
        } finally {
            setSaving(undefined);
        }
    };

    const saveApproval = () => save('approval', { ...savedCompany, approval });
    const saveCompany = (event: React.FormEvent) => {
        event.preventDefault();
        if (companyChanged && !eikInvalid) {
            setConfirming('company');
        }
    };

    const field = (key: keyof CompanyForm, label: string, maxLength: number) => (
        <Form.Group className='d-flex mb-2 justify-content-between align-items-center w-100'>
            <h6>{label}</h6>
            <div className='w-50'>
                <Form.Control
                    value={company[key]}
                    maxLength={maxLength}
                    isInvalid={key === 'eik' && eikInvalid}
                    disabled={!canEdit || !!saving}
                    onChange={(event) => setCompany((current) => ({ ...current, [key]: event.target.value }))}
                />
                {key === 'eik' && <Form.Control.Feedback type='invalid'>{t('eikInvalid')}</Form.Control.Feedback>}
            </div>
        </Form.Group>
    );

    const spinnerOr = (part: 'approval' | 'company', label: string) =>
        saving === part ? <Spinner animation='border' size='sm' /> : label;

    return (
        <section className='d-flex flex-column align-items-center p-4 w-100'>
            <div className='w-80p'>
                <h4 className='mb-2'>{t('waybillSettings')}</h4>
                <p>{t('waybillSettingsDescription')}</p>
                {isLoading && <Spinner animation='border' size='sm' />}
                {error && (
                    <Alert variant='danger'>
                        {error}{' '}
                        <Button type='button' size='sm' onClick={load}>
                            {t('reload')}
                        </Button>
                    </Alert>
                )}
                {settings && (
                    <>
                        {!canEdit && <Alert variant='warning' className='py-2'>{t('settingsReadOnly')}</Alert>}

                        <h5 className='mt-3'>{t('approvalMode')}</h5>
                        <Form.Group className='d-flex mb-2 justify-content-between align-items-center w-100'>
                            <h6>{t('approvalModeLabel')}</h6>
                            <div className='w-50 d-flex gap-2'>
                                <Form.Select
                                    value={approval}
                                    disabled={!canEdit || !!saving}
                                    onChange={(event) => setApproval(event.target.value as FleetApproval)}
                                >
                                    <option value='day'>{t('approvalDay')}</option>
                                    <option value='month'>{t('approvalMonth')}</option>
                                </Form.Select>
                                <Button
                                    type='button'
                                    className='text-nowrap'
                                    disabled={!canEdit || !approvalChanged || !!saving}
                                    onClick={() => setConfirming('approval')}
                                >
                                    {spinnerOr('approval', t('changeApproval'))}
                                </Button>
                            </div>
                        </Form.Group>
                        <p className='small text-secondary'>
                            {t(approval === 'month' ? 'approvalMonthHint' : 'approvalDayHint')}
                        </p>

                        <Form noValidate onSubmit={saveCompany}>
                            <h5 className='mt-3'>{t('waybillCompany')}</h5>
                            <Form.Group className='d-flex mb-2 justify-content-between align-items-center w-100'>
                                <h6>{t('companyName')}</h6>
                                <div className='w-50'>
                                    <Form.Control value={settings.name ?? ''} disabled readOnly />
                                </div>
                            </Form.Group>
                            {nameDiffers && (
                                <Alert variant='warning' className='py-2'>
                                    {t('companyNameDiffers', { ours: ourCompany?.companyName })}
                                </Alert>
                            )}
                            {field('eik', t('eik'), 13)}
                            {field('address', t('address'), 300)}
                            {field('manager', t('companyManager'), 120)}

                            {differsFromOurs && canEdit && (
                                <Alert variant='info' className='py-2 d-flex justify-content-between align-items-center gap-2'>
                                    <span>{t('companyDiffersFromOurs')}</span>
                                    <Button
                                        type='button'
                                        size='sm'
                                        variant='outline-primary'
                                        className='text-nowrap'
                                        disabled={!!saving}
                                        onClick={() => ours && setCompany(ours)}
                                    >
                                        {t('takeOurCompanyData')}
                                    </Button>
                                </Alert>
                            )}
                            <div className='d-flex justify-content-end align-items-center gap-2'>
                                {!companyChanged && canEdit && <span className='small text-secondary'>{t('noChanges')}</span>}
                                <Button
                                    type='button'
                                    variant='outline-secondary'
                                    disabled={!companyChanged || !!saving}
                                    onClick={() => setCompany(savedCompany)}
                                >
                                    {t('cancel')}
                                </Button>
                                <Button type='submit' disabled={!canEdit || !companyChanged || eikInvalid || !!saving}>
                                    {spinnerOr('company', t('saveCompanyToGpsAreon'))}
                                </Button>
                            </div>
                        </Form>
                    </>
                )}
            </div>

            <InfoModal
                show={!!confirming}
                title={t(confirming === 'company' ? 'confirmCompanyTitle' : 'confirmApprovalTitle')}
                translatedText={t('confirmSaveChanges')}
                buttonText='confirm'
                onHide={(confirmed) => {
                    const part = confirming;
                    setConfirming(undefined);
                    if (part === 'approval') {
                        if (confirmed) {
                            saveApproval();
                        } else {
                            setApproval(savedApproval);
                        }
                    } else if (part === 'company') {
                        if (confirmed) {
                            save('company', { ...company, approval: savedApproval });
                        } else {
                            setCompany(savedCompany);
                        }
                    }
                }}
            >
                {/* Само това, което се сменя: стара → нова стойност. Отказът връща записаното. */}
                <Table size='sm' className='mb-0 w-auto'>
                    <tbody>
                        {changes.map((change) => (
                            <tr key={change.label}>
                                <th className='pe-3'>{change.label}</th>
                                <td className='text-secondary text-decoration-line-through'>{change.from || t('emptyValue')}</td>
                                <td className='px-2'>→</td>
                                <td className='fw-bold'>{change.to || t('emptyValue')}</td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </InfoModal>
        </section>
    );
};
